import { ArrowForwardIos, CheckCircle, ZoomIn } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllGroupMessageEmailTemplates } from 'src/shared/services/groupMessageService';
import { Stack } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { useGlobals } from 'src/shared/contexts/appContext';
import { isDesignModeCompatible } from '../builder/parse';
import { GroupMessageEmailTemplate } from '../models/groupMessageEmailTemplate';
import { GroupMessageEmailTemplateView } from './groupMessageEmailTemplateView';
import { EmailTemplatePreviewModal } from './emailTemplatePreviewModal';

type EmailTemplatePickerModalProps = {
  open: boolean;
  openModal: (open: boolean) => void;
  // Om angiven: anropas istället för att navigera till create-from-template.
  // Används av t.ex. ComposeEmailModal som vill sätta body på sitt eget formulär.
  onTemplatePick?: (template: GroupMessageEmailTemplate) => void;
};

type TemplateTab = 'own' | 'system';

export const EmailTemplatePickerModal = (props: EmailTemplatePickerModalProps) => {
  const { open, openModal, onTemplatePick } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [emailTemplates, setEmailTemplates] = useState<GroupMessageEmailTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number>(0);
  const [previewTemplate, setPreviewTemplate] = useState<GroupMessageEmailTemplate | null>(null);
  const [tab, setTab] = useState<TemplateTab | null>(null);

  const { currentUser } = useGlobals();
  const [showOnlyFederation, setShowOnlyFederation] = useState(true);
  const canDistribute = (currentUser?.associations?.length ?? 0) > 1;

  // Positiva id:n = användarsparade mallar (DB). Negativa id:n = systemmallar (embedded HTML).
  const ownTemplates = useMemo(() => emailTemplates.filter((tmpl) => tmpl.id > 0), [emailTemplates]);
  const systemTemplates = useMemo(() => emailTemplates.filter((tmpl) => tmpl.id < 0), [emailTemplates]);

  // Default: "Era egna mallar" om det finns några, annars "Standardmallar".
  const activeTab: TemplateTab = tab ?? (ownTemplates.length > 0 ? 'own' : 'system');

  const visibleTemplates = useMemo(() => {
    if (activeTab === 'system') return systemTemplates;
    if (canDistribute && showOnlyFederation) {
      return ownTemplates.filter((tmpl) => tmpl.localAss_id === 0 && tmpl.countyAss_id === 0);
    }
    return ownTemplates;
  }, [activeTab, ownTemplates, systemTemplates, canDistribute, showOnlyFederation]);

  const navigate = useNavigate();

  const setEmailTemplate = () => {
    const template = emailTemplates.find((et) => et.id === selectedTemplateId);
    if (!template) return;
    if (onTemplatePick) {
      onTemplatePick(template);
      openModal(false);
      return;
    }
    navigate(`${RoutesEnum.GroupMessageEmailCreateFromTemplate}/${template.id}`);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllGroupMessageEmailTemplates();
      if (isMounted()) {
        setEmailTemplates(res.data);
      }
    } catch (e) {
      ConsoleHelper.log(e);
    } finally {
      setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const handleTabChange = (_: React.SyntheticEvent, value: TemplateTab) => {
    setTab(value);
    setSelectedTemplateId(0);
  };

  return (
    <>
      <GenericDialog
        open={open}
        onClose={() => openModal(false)}
        onSave={() => ConsoleHelper.log()}
        maxWidth='lg'
        dialogTitle={t('groupMessage:create.createEmailGroupMessage')}
        dialogContent={
          <Grid container spacing={3} justifyContent='flex-start'>
            <LoadingOverlay loading={loading} />
            <Grid item xs={12}>
              <Alert severity='info'>
                <AlertTitle>{t('groupMessage:create.selectTemplate')}</AlertTitle>
                {t('groupMessage:create.selectTemplateHint')}
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                  <Tab value='own' label={`${t('groupMessage:emailTemplates.tabs.own')} (${ownTemplates.length})`} />
                  <Tab value='system' label={`${t('groupMessage:emailTemplates.tabs.system')} (${systemTemplates.length})`} />
                </Tabs>
                {activeTab === 'own' && canDistribute && (
                  <Box sx={{ pb: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={showOnlyFederation}
                          onChange={(e) => {
                            setShowOnlyFederation(e.target.checked);
                            setSelectedTemplateId(0);
                          }}
                          size='small'
                        />
                      }
                      label={t('groupMessage:emailTemplates.showOnlyFederation')}
                    />
                  </Box>
                )}
              </Box>
            </Grid>

            {!loading && visibleTemplates.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant='body2'>
                    {activeTab === 'own' ? t('groupMessage:emailTemplates.noOwnTemplates') : t('groupMessage:emailTemplates.noSystemTemplates')}
                  </Typography>
                </Box>
              </Grid>
            )}

            {visibleTemplates.map((template) => {
              const isSelected = selectedTemplateId === template.id;
              const supportsDesignMode = isDesignModeCompatible(template.body) && !!template.body?.trim();
              return (
                <Grid item xs={6} sm={4} md={3} lg={2} key={template.id} sx={{ minWidth: '14rem', maxWidth: '16rem' }}>
                  <Box
                    role='button'
                    tabIndex={0}
                    onClick={() => setSelectedTemplateId(template.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedTemplateId(template.id);
                      }
                    }}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      cursor: 'pointer',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: isSelected ? 'primary.main' : (theme) => alpha(theme.palette.primary.main, 0.5),
                      overflow: 'hidden',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
                      transition: 'border-color 120ms ease, box-shadow 120ms ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        boxShadow: '0 4px 14px rgba(0,0,0,0.14)',
                      },
                      '&:focus-visible': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Tooltip title={t('groupMessage:emailTemplates.previewTooltip') as string}>
                      <IconButton
                        size='small'
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewTemplate(template);
                        }}
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          zIndex: 1,
                          p: 0.5,
                          borderRadius: '4px',
                          bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
                          border: '1px solid #dadde1',
                          '&:hover': {
                            bgcolor: 'background.paper',
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <ZoomIn fontSize='small' />
                      </IconButton>
                    </Tooltip>

                    <Box sx={{ p: 1.25, display: 'flex', justifyContent: 'center' }}>
                      <Box
                        sx={{
                          width: '100%',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: (theme) => alpha(theme.palette.text.primary, 0.08),
                          // Annars fångar iframe-elementet klick och de når aldrig den omslutande containern.
                          pointerEvents: 'none',
                        }}
                      >
                        <GroupMessageEmailTemplateView body={template.body} height='11rem' scale={0.32} />
                      </Box>
                    </Box>

                    <Grid
                      className={isSelected ? 'selected' : ''}
                      item
                      xs={12}
                      sx={{
                        width: '100%',
                        backgroundColor: isSelected ? (theme) => alpha(theme.palette.success.light, 0.2) : 'transparent',
                      }}
                    >
                      <Divider />

                      <Stack direction='column' alignItems='center' spacing={0.75} sx={{ p: 1 }}>
                        <Stack direction='row' alignItems='center' spacing={1}>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                            }}
                          >
                            {t(template.name)}
                          </Typography>
                          {isSelected && (
                            <CheckCircle
                              sx={{
                                width: '1.25rem',
                                height: '1.25rem',
                                opacity: 1,
                                color: (thm) => thm.palette.success.main,
                              }}
                            />
                          )}
                        </Stack>
                        <Chip
                          size='small'
                          label={supportsDesignMode ? t('groupMessage:builder.designModeBadge') : t('groupMessage:builder.directEditBadge')}
                          sx={{
                            height: 22,
                            fontSize: '0.75rem',
                            borderRadius: '4px',
                            bgcolor: supportsDesignMode
                              ? (theme) => alpha(theme.palette.primary.main, 0.1)
                              : (theme) => alpha(theme.palette.text.primary, 0.06),
                            color: supportsDesignMode ? 'primary.main' : 'text.secondary',
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      </Stack>
                    </Grid>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        }
        dialogActions={
          <Button
            variant='contained'
            color='primary'
            endIcon={<ArrowForwardIos fontSize='small' />}
            onClick={() => setEmailTemplate()}
            disableElevation
            disabled={!selectedTemplateId}
          >
            {t('common:button.continue')}
          </Button>
        }
      />
      {previewTemplate && (
        <EmailTemplatePreviewModal
          open={!!previewTemplate}
          onClose={() => setPreviewTemplate(null)}
          templateName={previewTemplate.name}
          body={previewTemplate.body}
        />
      )}
    </>
  );
};
